import dynamic from "next/dynamic";
import { type HeroFragment } from "~/gql/graphql";
import { graphql, useFragment, type FragmentType } from "~/gql/runtime";
import { __IS_DEV__ } from "~/features/app/utils";
import { ComplexHero } from "~/features/hero/complex-hero";
import { SecondaryHero } from "~/features/hero/secondary-hero";
import { PageHero } from "~/features/hero/page-hero";
import { EditorialHero } from "~/features/hero/editorial-hero";
import { SimpleHero } from "~/features/hero/simple-hero";
const ProductHeroMedia = dynamic(() => import("~/features/hero/product-hero/product-hero-media").then(mod => mod.ProductHeroMedia));
const ProductShowcase = dynamic(() => import("~/features/product-showcase").then(mod => mod.ProductShowcase));
const ProductHeroScrollableVideo = dynamic(() => import("~/features/hero/product-hero/product-hero-scrollable-video").then(mod => mod.ProductHeroScrollableVideo));
const ScrollableHero = dynamic(() => import("~/features/hero/scrollable-hero").then(mod => mod.ScrollableHero));
const Rev8Hero = dynamic(() => import("~/features/hero/rev8-hero").then(mod => mod.Rev8Hero));
const SensorConfiguratorHero = dynamic(() => import("~/features/hero/sensor-configurator-hero").then(mod => mod.SensorConfiguratorHero));
const SdkDownloadsHero = dynamic(() => import("~/features/hero/sdk-downloads-hero").then(mod => mod.SdkDownloadsHero));
const fragment = graphql(`
  fragment Hero on hero_NeoField {
    __typename
    ...ComplexHero
    ...SecondaryHero
    ...PageHero
    ...EditorialHero
    ...ProductHeroMedia
    ...ProductHeroScrollableVideo
    ...ScrollableHero
    ...SimpleHero
    ...CubeExperienceHero
    ...Rev8Hero
    ...SensorConfiguratorHero
    ...ProductShowcaseHero
    ...SdkDownloadsHero
  }
`);

/**
 * `hero_productShowcase_BlockType` is a synthetic typename minted by the Sanity
 * hero adapter to route content-rich `productHeroMedia` heroes to the
 * `ProductShowcase` layout; it is not a real Craft GraphQL typename, so widen
 * the key type to include it.
 */
type HeroTypename = HeroFragment["__typename"] | "hero_productShowcase_BlockType";
export function getHero(typeName: HeroTypename) {
  return typeName in heroes ? heroes[typeName] : null;
}
const heroes: Partial<Record<HeroTypename, React.ComponentType<any>>> = {
  hero_complexHero_BlockType: ComplexHero,
  hero_secondaryHero_BlockType: SecondaryHero,
  hero_pageHero_BlockType: PageHero,
  hero_editorialHero_BlockType: EditorialHero,
  hero_productHeroMedia_BlockType: ProductHeroMedia,
  hero_productShowcase_BlockType: ProductShowcase,
  hero_simpleHero_BlockType: SimpleHero,
  hero_productHeroScrollableVideo_BlockType: ProductHeroScrollableVideo,
  hero_scrollableHero_BlockType: ScrollableHero,
  hero_rev8Hero_BlockType: Rev8Hero,
  hero_sensorConfiguratorHero_BlockType: SensorConfiguratorHero,
  hero_sdkDownloadsHero_BlockType: SdkDownloadsHero
};
type Props = FragmentType<typeof fragment> & {
  block?: React.ReactNode;
};
export function Hero(props: Props) {
  const {
    block,
    ...rest
  } = props;
  const {
    __typename,
    ...hero
  } = useFragment(fragment, rest);
  const Component = getHero(__typename);
  if (!Component) {
    if (__IS_DEV__) {
      console.warn("Hero not found:", __typename);
    }
    return null;
  }
  return <Component block={block} {...hero} />;
}